import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Cta, the mobile commit CTA and THE app button.
 *
 * CSS selector: `mobile/atoms.css` `.ds-cta` (+ `--solid`, `--ignited`,
 * `--ghost`, `--danger`, `--disabled`, `--white`, `--frosted`, `--sm`, `--lg`,
 * `--xl`, `--pill`, `--block`).
 *
 * Doctrine: ink is command, accent is state (canon decision #1). Cta is
 * ink-filled, never accent-filled and never a gradient. Tone carries priority:
 * `solid` / `ignited` are ink, `ghost` is the outline secondary, `danger` is
 * the one permitted red fill and belongs to destructive commits only, and
 * `disabled` is a flat desaturated grey that withholds the affordance rather
 * than dimming it with opacity.
 *
 * `ignited` is a pure alias of `solid`, kept because the CSS defines both. It
 * reads better at the call site when a gated form transitions from grey to
 * black once it becomes valid.
 *
 * `white` and `frosted` are the auth cover-scene exception: static white
 * regardless of theme, for photographic hero scenes only.
 *
 * Sizes: default 48px for sheets, `sm` 44px for row actions, `lg` 52px for
 * pages, `xl` 56px for survey and auth. Height always comes from padding and
 * min-height, never from inflating the font (44px minimum tap target).
 */
const CTA_VARIANT = {
  solid: 'ds-cta--solid',
  ignited: 'ds-cta--ignited',
  ghost: 'ds-cta--ghost',
  danger: 'ds-cta--danger',
  disabled: 'ds-cta--disabled',
  white: 'ds-cta--white',
  frosted: 'ds-cta--frosted',
};

const CTA_SIZE = {
  base: null,
  sm: 'ds-cta--sm',
  lg: 'ds-cta--lg',
  xl: 'ds-cta--xl',
};

export const Cta = React.forwardRef(function Cta(
  {
    className,
    variant = 'solid',
    size = 'base',
    pill = false,
    block = false,
    type = 'button',
    disabled,
    ...rest
  },
  ref,
) {
  // `variant="disabled"` is a design-intent alias for the `disabled` prop. The
  // CSS shares one rule block between `.ds-cta--disabled` and `.ds-cta:disabled`,
  // so force the native attribute too and keyboard/AT users get real semantics.
  const isDisabled = Boolean(disabled) || variant === 'disabled';

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={cn(
        'ds-cta',
        CTA_VARIANT[variant],
        CTA_SIZE[size],
        pill && 'ds-cta--pill',
        block && 'ds-cta--block',
        className,
      )}
      {...rest}
    />
  );
});

/**
 * CtaRow, the side-by-side CTA pair for sheet footers and filter drill-ins.
 *
 * CSS selector: `mobile/atoms.css` `.ds-cta-row`.
 *
 * Doctrine: footer CTAs sit side by side, never stacked. The CSS already gives
 * every child `flex: 1` and the solid sibling `flex: 1.4` so the commit action
 * leads, so nothing needs to be passed down from here.
 */
export const CtaRow = React.forwardRef(function CtaRow({ className, ...rest }, ref) {
  return <div ref={ref} className={cn('ds-cta-row', className)} {...rest} />;
});

export default Cta;
